import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Confetti from 'react-confetti';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, Copy, Check, Info } from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showUppercaseWarning, setShowUppercaseWarning] = useState(false);
  const [copiedGmail, setCopiedGmail] = useState(false);
  const [helpPhoneNumber, setHelpPhoneNumber] = useState('');

  // Check for uppercase letters in email
  useEffect(() => {
    const hasUppercase = /[A-Z]/.test(email);
    setShowUppercaseWarning(hasUppercase && email.length > 0);
  }, [email]);

  // Fetch help phone number
  useEffect(() => {
    const fetchHelpNumber = async () => {
      try {
        const response = await axios.get(`${API}/settings/help-phone`);
        setHelpPhoneNumber(response.data.phone_number);
      } catch (error) {
        console.error('Failed to fetch help phone number:', error);
      }
    };
    fetchHelpNumber();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Login successful!');
      
      if (response.data.user.role === 'student') {
        sessionStorage.setItem('show_login_confetti', 'true');
      }
      
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const copyGmailDomain = () => {
    navigator.clipboard.writeText('@gmail.com');
    setCopiedGmail(true);
    toast.success('@gmail.com copied!');
    setTimeout(() => setCopiedGmail(false), 2000);
  };

  const fixEmailCase = () => {
    setEmail(email.toLowerCase());
    toast.success('Email converted to lowercase!');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-6 bg-gradient-to-br from-[#F7F7F7] to-[#E7E7E7]">
      <Card className="w-full max-w-md border-0 card-shadow rounded-2xl bg-white" data-testid="login-card">
        <CardHeader className="text-center pb-6 md:pb-8 pt-6 md:pt-8">
          <div className="flex justify-center mb-4 md:mb-6">
            <img 
              src="/gdiot-logo-new.png"
              alt="GDIOT Logo"
              className="h-12 md:h-16 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-xl md:text-2xl font-bold text-navy text-center">Welcome to GDIOT Family!</CardTitle>
          <CardDescription className="text-[#4D4D4D] mt-2 text-sm md:text-base px-2 md:px-0">Sign in to continue your learning journey</CardDescription>
        </CardHeader>
        <CardContent className="px-4 md:px-6">
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            {/* Email Input with Helpers */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm md:text-[15px] font-medium text-navy">GDIOT ID</Label>
              
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="name@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 md:h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth pr-20 md:pr-24 text-sm md:text-base"
                  data-testid="login-email-input"
                  aria-describedby="email-helper"
                />
                
                {/* @Gmail.com Copy Helper */}
                <button
                  type="button"
                  onClick={copyGmailDomain}
                  className="absolute right-1 md:right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1 px-2 md:px-3 py-1 md:py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 group"
                  title="Click to copy @gmail.com"
                  aria-label="Copy @gmail.com to clipboard"
                >
                  {copiedGmail ? (
                    <Check className="w-3 md:w-3.5 h-3 md:h-3.5 text-green-600" strokeWidth={2.5} />
                  ) : (
                    <Copy className="w-3 md:w-3.5 h-3 md:h-3.5 text-primary-blue" strokeWidth={2} />
                  )}
                  <span className="text-xs font-medium text-primary-blue">@gmail.com</span>
                </button>
              </div>

              {/* Uppercase Warning Popup */}
              {showUppercaseWarning && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-3 animate-slide-down">
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-orange-900">Please use small letters</p>
                      <p className="text-xs text-orange-800 mt-1">Enter everything in small letters (e.g., name@gmail.com)</p>
                      <Button
                        type="button"
                        onClick={fixEmailCase}
                        size="sm"
                        className="mt-2 h-7 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-lg"
                      >
                        Fix for me
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Keyboard Hints */}
              <div id="email-helper" className="text-xs text-[#4D4D4D] bg-blue-50 p-2 md:p-2.5 rounded-lg border border-blue-100">
                <p className="font-medium text-navy mb-1">💡 Quick Tip:</p>
                <p className="text-primary-blue font-medium">Click the blue &ldquo;<span className="font-bold">@gmail.com</span>&rdquo; button above to copy and paste it!</p>
                <p className="mt-1 text-[#4D4D4D]">Just type your mail given by GDIOT, then paste <span className="font-bold">@gmail.com</span> after it.</p>
              </div>
            </div>

            {/* Password Input with Visibility Toggle */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm md:text-[15px] font-medium text-navy">Password</Label>
              
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 md:h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth pr-12 text-sm md:text-base"
                  data-testid="login-password-input"
                  aria-label="Password"
                />
                
                {/* Password Visibility Toggle */}
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={0}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-[#4D4D4D]" strokeWidth={2} />
                  ) : (
                    <Eye className="w-5 h-5 text-[#4D4D4D]" strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 md:h-12 bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press mt-4 md:mt-6 text-sm md:text-base"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" strokeWidth={2} />
                  Sign In
                </>
              )}
            </Button>

            {/* Need Help Button */}
            {helpPhoneNumber && (
              <Button
                type="button"
                onClick={() => window.location.href = `tel:${helpPhoneNumber}`}
                className="w-full h-11 md:h-12 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-smooth btn-press mt-3 text-sm md:text-base"
              >
                <Info className="w-5 h-5 mr-2" strokeWidth={2} />
                Need Help?
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}